<script setup>
// Edição do contato a partir da conversa: usa o mesmo formulário da tela de Contatos
// (identificação, CPF/CNPJ, dados pessoais e endereço com CEP), sem os campos antigos de
// identificador, país, cidade e redes sociais.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  contact: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['cancel']);

const { t } = useI18n();
const store = useStore();
const uiFlags = useMapGetter('contacts/getUIFlags');
const getContactById = useMapGetter('contacts/getContactById');

const contactsFormRef = ref(null);
const contactData = ref({});
const avatarUrl = ref('');

const contactRecord = computed(() =>
  props.contact?.id ? getContactById.value(props.contact.id) : {}
);
const isUpdating = computed(() => uiFlags.value?.isUpdating);
const isFormInvalid = computed(() => contactsFormRef.value?.isFormInvalid);

const resetForm = () => {
  contactData.value = { ...contactRecord.value };
  avatarUrl.value = '';
};

watch(
  () => [props.show, props.contact?.id],
  ([show]) => {
    if (show) resetForm();
  },
  { immediate: true }
);

const onCancel = () => emit('cancel');

const handleFormUpdate = updatedData => {
  Object.assign(contactData.value, updatedData);
};

const errorMessage = error => {
  if (error instanceof DuplicateContactException) {
    const attributes = Array(error.data).map(String);
    if (attributes.includes('phone_number'))
      return t(
        'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.PHONE_NUMBER.DUPLICATE'
      );
    if (attributes.includes('email'))
      return t(
        'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.EMAIL_ADDRESS.DUPLICATE'
      );
  }
  if (error instanceof ExceptionWithMessage) return error.data;
  return t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.ERROR_MESSAGE');
};

const saveContact = async () => {
  if (isUpdating.value || isFormInvalid.value) return;
  const {
    id,
    name,
    email,
    phoneNumber,
    additionalAttributes,
    customAttributes,
  } = contactData.value;
  try {
    await store.dispatch('contacts/update', {
      id,
      name,
      email,
      phoneNumber,
      additionalAttributes,
      customAttributes,
    });
    await store.dispatch('contacts/fetchContactableInbox', id);
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.SUCCESS_MESSAGE'));
    onCancel();
  } catch (error) {
    useAlert(errorMessage(error));
  }
};

const handleAvatarUpload = async ({ file, url }) => {
  avatarUrl.value = url;
  try {
    await store.dispatch('contacts/update', {
      ...contactsFormRef.value?.state,
      id: props.contact.id,
      avatar: file,
      isFormData: true,
    });
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.SUCCESS_MESSAGE'));
  } catch {
    avatarUrl.value = '';
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.ERROR_MESSAGE'));
  }
};

const handleAvatarDelete = async () => {
  try {
    await store.dispatch('contacts/deleteAvatar', props.contact.id);
    avatarUrl.value = '';
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(
      error.message || t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.ERROR_MESSAGE')
    );
  }
};

// Restore Escape-to-close behavior that was provided by woot-modal before
// this drawer was reimplemented as a plain fixed panel.
useKeyboardEvents({
  Escape: {
    action: () => {
      if (props.show) onCancel();
    },
    allowOnFocusedInput: true,
  },
});
</script>

<template>
  <transition
    enter-active-class="transition duration-200 ease-out"
    enter-from-class="ltr:translate-x-full rtl:-translate-x-full opacity-0"
    leave-active-class="transition duration-150 ease-in"
    leave-to-class="ltr:translate-x-[30%] rtl:-translate-x-[30%] opacity-0"
  >
    <div
      v-if="show"
      class="fixed inset-y-0 ltr:right-0 rtl:left-0 z-50 flex flex-col w-[34rem] max-w-full h-full bg-n-surface-2 ltr:border-l rtl:border-r border-n-weak shadow-lg"
      data-test="edit-contact-panel"
    >
      <div class="flex items-start justify-between gap-3 px-6 pt-6 pb-4">
        <div class="flex min-w-0 items-center gap-3">
          <Avatar
            :src="avatarUrl || contactRecord.thumbnail || ''"
            :name="contactRecord.name || ''"
            :size="48"
            allow-upload
            @upload="handleAvatarUpload"
            @delete="handleAvatarDelete"
          />
          <div class="min-w-0">
            <h2 class="mb-0.5 truncate text-lg font-medium text-n-slate-12">
              {{ $t('EDIT_CONTACT.TITLE') }}
            </h2>
            <p
              class="mb-0 truncate text-sm text-n-slate-11"
              :title="contactRecord.name || contactRecord.email"
            >
              {{ contactRecord.name || contactRecord.email }}
            </p>
          </div>
        </div>
        <Button icon="i-lucide-x" slate ghost sm @click="onCancel" />
      </div>
      <div class="min-h-0 flex-1 overflow-y-auto px-6 pb-4">
        <ContactsForm
          ref="contactsFormRef"
          :contact-data="contactData"
          is-details-view
          hide-social-links
          @update="handleFormUpdate"
        />
      </div>
      <footer
        class="flex justify-end gap-2 border-t border-n-weak bg-n-surface-2 px-6 py-3"
      >
        <Button
          :label="$t('CONTACT_FORM.FORM.CANCEL')"
          slate
          faded
          sm
          @click="onCancel"
        />
        <Button
          :label="$t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.UPDATE_BUTTON')"
          sm
          :is-loading="isUpdating"
          :disabled="isUpdating || isFormInvalid"
          data-test="edit-contact-save"
          @click="saveContact"
        />
      </footer>
    </div>
  </transition>
</template>
